import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '../../lib/api/client'
import type { CatalogResultCard } from './types'

/** Page size for the cursor-following fetch below — generous enough that
 * almost every series (357/520 hold exactly 60 rows per ADR-0099) resolves
 * in a single request, while still bounding any one request's payload. */
const PAGE_SIZE = 100

/**
 * The narrow seam Surface A's per-series table page needs (UID-34c /
 * ADR-0099 Gap 2): ALL real result-card rows behind a series, fetched via
 * the series-scoped `GET /catalog/search` and followed to completion via its
 * existing `nextCursor` pagination (UID-227) — mirroring the cursor-follow
 * shape `useCatalogSearch.ts`'s `goToNextPage` already implements, just
 * driven to exhaustion here instead of one "load more" click at a time.
 */
export interface CatalogSeriesRowsService {
  fetchPage(
    seriesCode: string,
    cursor: string | null,
  ): Promise<{ readonly items: readonly CatalogResultCard[]; readonly nextCursor: string | null }>
}

function problemTitle(error: unknown, fallback: string): string {
  return typeof error === 'object' && error !== null && 'title' in error
    ? String((error as { title?: unknown }).title)
    : fallback
}

async function bffFetchPage(
  seriesCode: string,
  cursor: string | null,
): Promise<{ readonly items: readonly CatalogResultCard[]; readonly nextCursor: string | null }> {
  const { data, error } = await apiClient.GET('/catalog/search', {
    params: {
      query: {
        series: seriesCode,
        limit: PAGE_SIZE,
        ...(cursor !== null ? { cursor } : {}),
      },
    },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t load this series.'))
  }
  return { items: data.items, nextCursor: data.nextCursor }
}

export const defaultCatalogSeriesRowsService: CatalogSeriesRowsService = {
  fetchPage: bffFetchPage,
}

export type CatalogSeriesRowsState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  /** An empty `rows` array means the series-scoped search returned nothing —
   * an honest "nothing on file for this series", never an error. */
  | { readonly status: 'ready'; readonly rows: readonly CatalogResultCard[] }

export type UseCatalogSeriesRowsResult = CatalogSeriesRowsState & {
  readonly retry: () => void
}

/**
 * Data-layer seam for `CatalogSeriesConfigurePage` (Surface A, now a table
 * per ADR-0099 Gap 2). Fetches EVERY real result-card row behind
 * `seriesCode` — following `nextCursor` to completion rather than capping at
 * one row (the corrected `limit: 1` scope-cut, see `useCatalogSeriesFamily.ts`,
 * now deleted) — and re-fetches from the first page when `seriesCode`
 * changes or on `retry()`. Same three-state shape as the singular hook it
 * supersedes, just plural.
 */
export function useCatalogSeriesRows(
  seriesCode: string,
  service: CatalogSeriesRowsService = defaultCatalogSeriesRowsService,
): UseCatalogSeriesRowsResult {
  const [state, setState] = useState<CatalogSeriesRowsState>({ status: 'loading' })
  const serviceRef = useRef(service)
  serviceRef.current = service

  const load = useCallback(() => {
    setState({ status: 'loading' })
    let cancelled = false

    async function run() {
      const rows: CatalogResultCard[] = []
      let cursor: string | null = null
      do {
        const page = await serviceRef.current.fetchPage(seriesCode, cursor)
        rows.push(...page.items)
        cursor = page.nextCursor
      } while (cursor !== null && !cancelled)
      if (!cancelled) setState({ status: 'ready', rows })
    }

    run().catch((error: unknown) => {
      if (cancelled) return
      const message = error instanceof Error ? error.message : 'We couldn’t load this series.'
      setState({ status: 'error', message })
    })

    return () => {
      cancelled = true
    }
    // `service` is read only via the ref, so `seriesCode` is the only real dependency.
  }, [seriesCode])

  useEffect(() => load(), [load])

  return { ...state, retry: load }
}
